import RefreshIcon from "@mui/icons-material/Refresh";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { useGetAllMarketForDeliveryOffices } from "../../../../../api/services/reports/reportsApi";
import { useMediaQueries } from "../../../../../hooks/useMediaQueries";
import { useMultiSelectARIALabels } from "../../../../../hooks/useMultiSelectARIALabels";
import {
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../../helpers/utils";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../redux/slices/appState/appStateSlice";
import { reportsSearchReportsSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiBox,
  MuiButtonPrimary,
  MuiButtonTertiary,
  MuiGrid,
  MuiMultiSelect,
} from "../../../../../components";
import { SearchNotificationStatusReportFormKeys } from "../../../modelsAndSchemas/models";
import { useGridContainer } from "../../../../../hooks/useGridContainer";
import { PendingBillingSearchFormWrapper } from "../../../ReportSearchStyle";

export interface NotificationStatusReportFormRef {
  reset: () => void;
}
export interface NotificationStatusReportFormProps {
  getNotificationStatusReports: (params: {
    marketIds: string[];
  }) => void;
}
export const NotificationStatusReportForm = forwardRef<
  NotificationStatusReportFormRef,
  NotificationStatusReportFormProps
>(({ getNotificationStatusReports }, ref) => {
  const { gridSizeMedium: gridSize } = useGridContainer();
  const { fetchComponentTitle, commonLabels, useFetchLabel } = useSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const officeRef = useRef<{ resetValues: (values?: string[]) => void }>(null);

  const { isMobile } = useMediaQueries();

  const {
    isPending: isGetMarketDeliveryOfficesPending,
    isError: isGetMarketDeliveryOfficesError,
    data: marketList,
  } = useGetAllMarketForDeliveryOffices();

  const { reset, control, getValues } = useFormContext();
  const dispatch = useDispatch();

  useImperativeHandle(ref, () => ({
    reset: handleClearFilter,
  }));

  const setDefaultMarketValue = () => {
    if (isNotEmpty(marketList)) {
      const marketOptions = setApiDataToDropDownOption(marketList);
      const marketValues = marketOptions.map((option) => option.value);
      reset({
        [SearchNotificationStatusReportFormKeys.marketOfDeliveryOffices]:
          marketValues,
      });
      officeRef.current?.resetValues(marketValues);
    }
  };

  const marketListData = useMemo(() => {
    const selectedTemplate = isNotEmpty(marketList)
      ? setApiDataToDropDownOption(marketList).map((option) => option.value)
      : [];
    setDefaultMarketValue();

    return {
      deliveryMarketListDropdown: setApiDataToDropDownOption(marketList),
      selectedTemplate,
    };
  }, [marketList]);

  useEffect(() => {
    if (isGetMarketDeliveryOfficesPending) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    } else {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isGetMarketDeliveryOfficesPending]);

  useEffect(() => {
    if (isGetMarketDeliveryOfficesError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }
  }, [isGetMarketDeliveryOfficesError]);

  const handleClearFilter = () => {
    setDefaultMarketValue();
    const { marketOfDeliveryOffices } = getValues();
    getNotificationStatusReports({
      marketIds: marketOfDeliveryOffices,
    });
  };

  const getLabel = useFetchLabel(reportsSearchReportsSiteCoreKeys.header);

  const renderTitle = () => (
    <AccordionHeaderText
      label={fetchComponentTitle(reportsSearchReportsSiteCoreKeys.header)}
    />
  );

  const renderNotificationStatusFromHeader = () => {
    return (
      <MuiGrid container className="form-header">
        <MuiGrid justifySelf="end" className="form-header-right">
          <MuiButtonTertiary onClick={handleClearFilter} id={"reset-button-notification"}>
            <RefreshIcon /> {commonLabels.resetAllSearch}
          </MuiButtonTertiary>
        </MuiGrid>
      </MuiGrid>
    );
  };

  return (
    <PendingBillingSearchFormWrapper>
      <AccordionHeader showExpandCollapseIcon={true} title={renderTitle()}>
        {renderNotificationStatusFromHeader()}
        <MuiBox className="selectReportFieldContainer" data-testid="notification-status-market-select">
          <MuiGrid container columnSpacing={2} rowSpacing={isMobile ? 2 : 4}>
            <MuiGrid size={gridSize}>
              <FormController
                control={control}
                name={
                  SearchNotificationStatusReportFormKeys.marketOfDeliveryOffices
                }
                ref={officeRef}
              >
                <MuiMultiSelect
                  options={marketListData.deliveryMarketListDropdown}
                  selectedValues={marketListData.selectedTemplate}
                  label={getLabel(
                    reportsSearchReportsSiteCoreKeys.marketForDeliveryOfficeDropDown,
                  )}
                  showSearch
                  showChip
                  id={
                    SearchNotificationStatusReportFormKeys.marketOfDeliveryOffices
                  }
                  chipId={`${SearchNotificationStatusReportFormKeys.marketOfDeliveryOffices}-chip`}
                  selectPlaceHolder={commonLabels.selectPlaceHolder}
                  searchPlaceHolder={commonLabels.search}
                  selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                  doneText={commonLabels.done}
                  allARIAProps={multiSelectARIALabels}
                  showSelectAllOption
                  isMandatory
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
        <MuiBox className="selectReportButtonContainer" textAlign="right">
          <MuiButtonPrimary type="submit">
            {commonLabels.search}
          </MuiButtonPrimary>
        </MuiBox>
      </AccordionHeader>
    </PendingBillingSearchFormWrapper>
  );
});
